import { HttpClient } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Observable } from 'rxjs';
import { environment } from '../../../environments/environment';
import { PlatformPauseDuration } from '../enums/platform-pause-duration.enum';
import {
  PlatformApiInvitation,
  PlatformApiProfile,
  PlatformApiRecommendation,
  PlatformApiSavedJob,
  PlatformApplication,
  PlatformExtractedProfile,
  PlatformImportableApplication,
  PlatformMe,
  PlatformProfileUpdate,
} from '../models/platform.models';

export interface PlatformApiImportedCv {
  applicationId: number;
  uploadId: number;
  name: string;
}

export interface PlatformApplyResult {
  applicationId: number;
  invitationAccepted: boolean;
}

@Injectable({
  providedIn: 'root',
})
export class PlatformCandidateApiService {
  private readonly http = inject(HttpClient);
  private readonly base = `${environment.apiBaseUrl}/candidate/platform`;

  /**
   * Loads the lightweight account/session of the logged-in candidate.
   * @returns Observable of the me view
   */
  me(): Observable<PlatformMe> {
    return this.http.get<PlatformMe>(`${this.base}/me`);
  }

  /**
   * Loads the full editable profile of the logged-in candidate.
   * @returns Observable of the profile view
   */
  getProfile(): Observable<PlatformApiProfile> {
    return this.http.get<PlatformApiProfile>(`${this.base}/profile`);
  }

  /**
   * Saves profile fields.
   * @param body - Changed profile fields
   * @returns Observable of the updated profile view
   */
  updateProfile(body: PlatformProfileUpdate): Observable<PlatformApiProfile> {
    return this.http.put<PlatformApiProfile>(`${this.base}/profile`, body);
  }

  /**
   * Uploads the application photo.
   * @param file - Image file
   * @returns Observable of the updated profile view
   */
  uploadPhoto(file: File): Observable<PlatformApiProfile> {
    const form = new FormData();
    form.append('image', file, file.name);
    return this.http.post<PlatformApiProfile>(`${this.base}/profile/photo`, form);
  }

  /**
   * Removes the application photo.
   * @returns Observable of the updated profile view
   */
  deletePhoto(): Observable<PlatformApiProfile> {
    return this.http.delete<PlatformApiProfile>(`${this.base}/profile/photo`);
  }

  /**
   * Adds a document to the profile.
   * @param file - Document
   * @param type - Document type
   * @param main - Mark as main CV
   * @returns Observable of the updated profile view
   */
  uploadDocument(
    file: File,
    type: 'cv' | 'cover-letter' | 'certificate' | 'other',
    main = false
  ): Observable<PlatformApiProfile> {
    const form = new FormData();
    form.append('file', file, file.name);
    form.append('type', type);
    form.append('main', String(main));
    return this.http.post<PlatformApiProfile>(`${this.base}/documents`, form);
  }

  /**
   * Parses a CV into profile data and stores it as main CV.
   * @param file - CV
   * @returns Observable of the extracted profile
   */
  parseCv(file: File): Observable<PlatformExtractedProfile> {
    const form = new FormData();
    form.append('file', file, file.name);
    return this.http.post<PlatformExtractedProfile>(`${this.base}/documents/parse-cv`, form);
  }

  /**
   * Removes a document from the profile.
   * @param uploadId - Upload id
   * @returns Observable of the updated profile view
   */
  deleteDocument(uploadId: number): Observable<PlatformApiProfile> {
    return this.http.delete<PlatformApiProfile>(`${this.base}/documents/${uploadId}`);
  }

  /**
   * Marks a CV as main CV.
   * @param uploadId - Upload id
   * @returns Observable of the updated profile view
   */
  setMainDocument(uploadId: number): Observable<PlatformApiProfile> {
    return this.http.patch<PlatformApiProfile>(`${this.base}/documents/${uploadId}/main`, {});
  }

  /**
   * Updates a profile document type and/or main-CV flag.
   * @param uploadId - Upload id
   * @param body - Type and/or main flag
   * @returns Observable of the updated profile view
   */
  updateDocument(
    uploadId: number,
    body: { type?: 'cv' | 'cover-letter' | 'certificate' | 'other'; main?: boolean }
  ): Observable<PlatformApiProfile> {
    return this.http.patch<PlatformApiProfile>(`${this.base}/documents/${uploadId}`, body);
  }

  /**
   * Signed download link for a document.
   * @param uploadId - Upload id
   * @returns Observable with the url
   */
  documentUrl(uploadId: number): Observable<{ url: string }> {
    return this.http.get<{ url: string }>(`${this.base}/documents/${uploadId}/url`);
  }

  /**
   * CVs from connected applications that can be taken over.
   * @returns Observable of imported CVs
   */
  importedCvs(): Observable<PlatformApiImportedCv[]> {
    return this.http.get<PlatformApiImportedCv[]>(`${this.base}/documents/imported-cvs`);
  }

  /**
   * Takes over CVs from connected applications.
   * @param uploads - Chosen uploads
   * @returns Observable of the updated profile view
   */
  adoptDocuments(
    uploads: {
      applicationId: number;
      uploadId: number;
      type: 'cv' | 'cover-letter' | 'certificate' | 'other';
      main?: boolean;
    }[]
  ): Observable<PlatformApiProfile> {
    return this.http.post<PlatformApiProfile>(`${this.base}/documents/adopt`, { uploads });
  }

  /**
   * Finishes onboarding.
   * @returns Observable of the updated me view
   */
  completeOnboarding(): Observable<PlatformMe> {
    return this.http.post<PlatformMe>(`${this.base}/onboarding/complete`, {});
  }

  /**
   * Loads applications the candidate account is allowed to track.
   * @returns Observable of tracked applications
   */
  applications(): Observable<PlatformApplication[]> {
    return this.http.get<PlatformApplication[]>(`${this.base}/applications`);
  }

  /**
   * Loads person-originated applications that can still be imported.
   * @returns Observable of importable applications
   */
  importableApplications(): Observable<PlatformImportableApplication[]> {
    return this.http.get<PlatformImportableApplication[]>(
      `${this.base}/importable-applications`
    );
  }

  /**
   * Keeps the selected applications and hides the rest from the platform.
   * @param applicationIds - Application ids the candidate wants to track
   * @returns Observable that completes when the choice is stored
   */
  importApplications(applicationIds: number[]): Observable<void> {
    return this.http.post<void>(`${this.base}/applications/import`, { applicationIds });
  }

  /**
   * Removes an application from tracking.
   * @param applicationId - Application id
   * @returns Observable that completes when removed
   */
  removeFromTracking(applicationId: number): Observable<void> {
    return this.http.delete<void>(`${this.base}/applications/${applicationId}/tracking`);
  }

  /**
   * Submits follow-up data for an incomplete application.
   * @param applicationId - Application id
   * @param form - Multipart payload (missing-data fields and files)
   * @returns Observable of the missing-data response
   */
  followUp(applicationId: number, form: FormData): Observable<unknown> {
    return this.http.post<unknown>(`${this.base}/applications/${applicationId}/follow-up`, form);
  }

  /**
   * Applies to a job with the profile.
   * @param jobId - Job id
   * @param form - Multipart payload (overrides and files)
   * @returns Observable of the apply result
   */
  applyWithProfile(jobId: number, form: FormData): Observable<PlatformApplyResult> {
    return this.http.post<PlatformApplyResult>(`${this.base}/jobs/${jobId}/apply`, form);
  }

  /**
   * Saved jobs.
   * @returns Observable of saved jobs
   */
  savedJobs(): Observable<PlatformApiSavedJob[]> {
    return this.http.get<PlatformApiSavedJob[]>(`${this.base}/saved-jobs`);
  }

  /**
   * Bookmarks a job.
   * @param jobId - Job id
   * @returns Observable that completes when saved
   */
  saveJob(jobId: number): Observable<void> {
    return this.http.post<void>(`${this.base}/saved-jobs`, { jobId });
  }

  /**
   * Removes a bookmark.
   * @param jobId - Job id
   * @returns Observable that completes when removed
   */
  unsaveJob(jobId: number): Observable<void> {
    return this.http.delete<void>(`${this.base}/saved-jobs/${jobId}`);
  }

  /**
   * Open Talenthub invitations.
   * @returns Observable of invitations
   */
  invitations(): Observable<PlatformApiInvitation[]> {
    return this.http.get<PlatformApiInvitation[]>(`${this.base}/invitations`);
  }

  /**
   * Declines an invitation.
   * @param jobId - Job id
   * @returns Observable that completes when declined
   */
  declineInvitation(jobId: number): Observable<void> {
    return this.http.post<void>(`${this.base}/invitations/${jobId}/decline`, {});
  }

  /**
   * Removes an invitation for a closed job.
   * @param jobId - Job id
   * @returns Observable that completes when removed
   */
  removeInvitation(jobId: number): Observable<void> {
    return this.http.delete<void>(`${this.base}/invitations/${jobId}`);
  }

  /**
   * Recommended jobs.
   * @returns Observable of recommendations
   */
  recommendations(): Observable<PlatformApiRecommendation[]> {
    return this.http.get<PlatformApiRecommendation[]>(`${this.base}/recommendations`);
  }

  /**
   * Stores notification preferences.
   * @param body - Preferences
   * @returns Observable that completes when stored
   */
  updateNotifications(body: {
    notifyTalentpoolInvitation: boolean;
    notifyJobRecommendation: boolean;
    notifyApplicationStatus: boolean;
  }): Observable<void> {
    return this.http.put<void>(`${this.base}/settings/notifications`, body);
  }

  /**
   * Pauses or resumes recommendations.
   * @param duration - Pause duration or null to resume
   * @returns Observable that completes when stored
   */
  pauseRecommendations(duration: PlatformPauseDuration | null): Observable<void> {
    return this.http.put<void>(`${this.base}/settings/recommendation-pause`, { duration });
  }

  /**
   * Changes or sets the password.
   * @param newPassword - New password
   * @param currentPassword - Current password when one exists
   * @param tfa - TOTP code when 2FA is enabled
   * @returns Observable that completes when changed
   */
  changePassword(newPassword: string, currentPassword?: string, tfa?: string): Observable<void> {
    return this.http.post<void>(`${this.base}/settings/password`, {
      newPassword,
      currentPassword: currentPassword || undefined,
      tfa,
    });
  }

  /**
   * Leaves the Talenthub.
   * @returns Observable that completes when done
   */
  leaveTalenthub(): Observable<void> {
    return this.http.post<void>(`${this.base}/talenthub/leave`, {});
  }

  /**
   * Joins the Talenthub.
   * @returns Observable that completes when done
   */
  joinTalenthub(): Observable<void> {
    return this.http.post<void>(`${this.base}/talenthub/join`, {});
  }

  /**
   * Deletes the platform account.
   * @returns Observable that completes when deleted
   */
  deleteAccount(): Observable<void> {
    return this.http.delete<void>(`${this.base}/account`);
  }
}
